import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, CheckCircle2, XCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

const formatSGD = (cents) => `S$${(Number(cents || 0) / 100).toFixed(2)}`;

const AdminMarketplacePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (status) => {
    setLoading(true);
    try {
      const r = await api.adminListMarketplace(status);
      setList(r.worksheets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(tab); }, [tab, refresh]);

  const approve = async (id) => {
    try { await api.adminApproveMarketplace(id); toast.success('Approved'); await refresh(tab); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Rejection reason (shown to tutor):');
    if (reason === null) return;
    try { await api.adminRejectMarketplace(id, reason); toast.success('Rejected'); await refresh(tab); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openPdf = (id) => {
    const token = localStorage.getItem('token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/worksheets/${id}/pdf?token=${encodeURIComponent(token)}`, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="admin-mkt-back">
            <ArrowLeft className="w-4 h-4" /> Admin
          </button>
          <div className="font-semibold text-slate-800">Marketplace vetting</div>
          <div className="text-sm text-slate-600">{user?.email}</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 pt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="admin-mkt-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="admin-mkt-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="admin-mkt-tab-rejected">Rejected</TabsTrigger>
          </TabsList>

          {['pending','approved','rejected'].map((s) => (
            <TabsContent key={s} value={s} className="mt-4 space-y-3">
              {loading && <div className="text-slate-500">Loading…</div>}
              {!loading && list.length === 0 && (
                <Card className="p-8 text-center text-slate-500 rounded-2xl">No {s} worksheets.</Card>
              )}
              {!loading && list.map((w) => (
                <Card key={w.id} className="p-4 rounded-2xl" data-testid={`admin-mkt-item-${w.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge>{w.level}</Badge>
                        <Badge variant="secondary">{w.status}</Badge>
                        <span className="text-xs text-slate-500">{w.num_questions} qns</span>
                        <span className="text-xs text-slate-500">by {w.tutor_name}</span>
                      </div>
                      <div className="font-semibold text-slate-900">{w.title}</div>
                      {w.description && <div className="text-sm text-slate-600">{w.description}</div>}
                      {w.rejection_reason && <div className="text-sm text-rose-700 mt-1">Reason: {w.rejection_reason}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatSGD(w.price_cents)}</div>
                      <div className="text-xs text-slate-500">{new Date(w.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPdf(w.id)} data-testid={`admin-mkt-view-${w.id}`}>
                        <Download className="w-4 h-4 mr-1" /> View PDF
                      </Button>
                      {w.status !== 'approved' && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(w.id)} data-testid={`admin-mkt-approve-${w.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {w.status !== 'rejected' && (
                        <Button size="sm" variant="outline" className="text-rose-600 border-rose-300" onClick={() => reject(w.id)} data-testid={`admin-mkt-reject-${w.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminMarketplacePage;
